import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IntlProvider } from "react-intl";
import { useApplicationInsights } from "@packsize/application-monitoring";
import { packsizeHooks } from "@packsize/utils";
import type { z } from "zod";

import { createLocaleSelectionSchema, type Locale, type LocaleSelection, type SelectionSource } from "../../schemas";
import type { LocaleDefinition } from "../../types";

import { LocaleContext } from "./locale.context";

interface LocalizationProviderProps<TLocale extends Locale> {
	readonly locales: LocaleDefinition<TLocale>[];
	readonly defaultLocale: TLocale;
	readonly localeSchema: z.ZodType<TLocale>;
	readonly storagePrefix: string;
	readonly children: React.ReactNode;
}

export const LocalizationProvider = <TLocale extends Locale>({
	locales,
	defaultLocale,
	localeSchema,
	storagePrefix,
	children,
}: LocalizationProviderProps<TLocale>): React.ReactElement => {
	const defaultLocaleSelection: LocaleSelection<TLocale> = useMemo(
		() => ({ locale: defaultLocale, source: "system" }),
		[defaultLocale],
	);
	const localeSelectionSchema = useMemo(() => createLocaleSelectionSchema(localeSchema), [localeSchema]);

	const { value: locale = defaultLocaleSelection, set: setCurrentLocale } = packsizeHooks.useTypedLocalStorage(
		`${storagePrefix}.locale`,
		localeSelectionSchema,
		defaultLocaleSelection,
	);

	const [messages, setMessages] = useState<Record<string, string>>({});

	const appInsights = useApplicationInsights();

	const getLocaleDefinition = useCallback(
		(l: TLocale): LocaleDefinition<TLocale> | undefined => locales.find((def) => def.key === l),
		[locales],
	);

	useEffect(() => {
		const currentLocaleDefinition = getLocaleDefinition(locale.locale);
		const defaultLocaleDefinition = getLocaleDefinition(defaultLocale);

		const localeDefinition = currentLocaleDefinition ?? defaultLocaleDefinition;

		if (localeDefinition === undefined) {
			appInsights.trackException({
				exception: new Error(`Locale definition not found for default locale: ${String(defaultLocale)}`),
				properties: { defaultLocale, currentLocale: locale.locale },
			});
			return;
		}

		if (currentLocaleDefinition === undefined) {
			appInsights.trackEvent({
				name: "Locale.LocaleFallback",
				properties: { locale: locale.locale, defaultLocale },
			});
		}

		let cancelled = false;

		const loadMessages = async () => {
			const loaded = await localeDefinition.loader();

			if (cancelled === false) {
				setMessages(loaded.default);
			}
		};

		loadMessages();

		return () => {
			cancelled = true;
		};
	}, [appInsights, locale, defaultLocale, getLocaleDefinition]);

	const setLocale = useCallback(
		(value: unknown, source: Exclude<SelectionSource, "system">) => {
			const result = localeSchema.safeParse(value);

			if (result.success === false) {
				appInsights.trackException({
					exception: new Error(`Unsupported locale: ${String(value)}`),
					properties: { locale: value },
				});

				setCurrentLocale({
					locale: defaultLocale,
					source: "system",
				});
				return;
			}

			setCurrentLocale({
				locale: result.data,
				source,
			});
		},
		[appInsights, defaultLocale, localeSchema, setCurrentLocale],
	);

	const contextValue = useMemo(
		() => ({
			currentLocale: locale,
			locales,
			setLocale,
		}),
		[locale, locales, setLocale],
	);

	const onErrorHandler = useCallback(
		(error: Error) => {
			if (import.meta.env?.MODE === "test") {
				return;
			}

			appInsights.trackEvent({
				name: "Locale.MissingTranslation",
				properties: {
					locale: locale.locale,
					message: error.message,
				},
			});
		},
		[appInsights, locale],
	);

	return (
		<LocaleContext.Provider value={contextValue}>
			<IntlProvider locale={locale.locale} messages={messages} onError={onErrorHandler}>
				{children}
			</IntlProvider>
		</LocaleContext.Provider>
	);
};
